import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, X, CheckCircle2, Zap, Target, Star, MousePointer2, Sparkles, Flame, Trophy } from 'lucide-react';
import { cn } from '../lib/utils';
import { CloudIcon, ShellIcon, StarIcon as BurstIcon, GoldRushIcon } from './ModeIcons';

interface Step {
  title: string;
  description: string;
  visual: React.ReactNode;
}

interface HowToPlayProps {
  mode: 'duel' | 'sum10' | 'apple' | 'goldrush';
  onClose: () => void;
}

const VisualDuel1 = () => (
  <div className="flex items-center gap-3">
    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-3xl font-num clay-base text-slate-900 border-none shadow-lg">12</div>
    <div className="text-[#0ea5e9] font-num text-xl italic drop-shadow-sm">VS</div>
    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-3xl font-num clay-base text-slate-900 border-none shadow-lg">8</div>
  </div>
);

const VisualDuel2 = () => (
  <div className="relative">
    <div className="w-20 h-20 bg-sky-400 rounded-3xl flex items-center justify-center text-4xl font-num text-white clay-base border-none shadow-xl">
      <CloudIcon className="w-12 h-12 absolute opacity-20" />
      <span className="relative z-10">12</span>
    </div>
    <motion.div 
      animate={{ scale: [1, 1.2, 1], y: [0, -5, 0] }}
      transition={{ repeat: Infinity, duration: 1.5 }}
      className="absolute -bottom-2 -right-4"
    >
      <MousePointer2 className="w-10 h-10 text-slate-900 fill-white drop-shadow-md" />
    </motion.div>
  </div>
);

const VisualFever = ({ mode }: { mode: string }) => (
  <div className="relative flex flex-col items-center">
    <div className="flex gap-1 mb-4">
      {[...Array(10)].map((_, i) => (
        <motion.div 
          key={i}
          animate={{ 
            backgroundColor: i < 10 ? (mode === 'goldrush' ? "#fbbf24" : "#f43f5e") : "#e2e8f0",
            scale: i < 10 ? [1, 1.1, 1] : 1
          }}
          transition={{ delay: i * 0.05, repeat: Infinity, duration: 2 }}
          className="w-2 h-6 rounded-full"
        />
      ))}
    </div>
    <div className="relative">
      <Flame className="w-16 h-16 text-rose-500 fill-rose-500 animate-bounce" />
      <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-400 animate-pulse" />
    </div>
  </div>
);

const VisualSum1 = () => (
  <div className="flex gap-2 relative p-4">
    {[3, 4, 3].map((v, i) => (
      <div key={i} className={cn(
        "w-12 h-12 rounded-full flex items-center justify-center text-xl font-num clay-base border-none shadow-md",
        "bg-white text-slate-900"
      )}>{v}</div>
    ))}
    <motion.div 
      initial={{ width: 0 }}
      animate={{ width: '100%' }}
      transition={{ repeat: Infinity, duration: 2 }}
      className="absolute top-1/2 left-4 h-2 bg-sky-400/30 -translate-y-1/2 z-[-1] rounded-full"
    />
  </div>
);

const VisualSum2 = () => (
  <div className="relative w-16 h-16">
    <motion.div
      animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
      transition={{ repeat: Infinity, duration: 2 }}
      className="w-full h-full bg-rose-500 rounded-full flex items-center justify-center text-4xl font-num text-white clay-base border-none shadow-lg"
    >
      9
    </motion.div>
    <Zap className="absolute -top-2 -right-2 w-8 h-8 text-amber-400 fill-amber-400 drop-shadow-md" />
  </div>
);

const VisualApple1 = () => (
  <div className="relative p-4 grid grid-cols-2 gap-3 bg-slate-900/5 rounded-3xl">
    {[2, 3, 2, 3].map((v, i) => (
      <div key={i} className="w-12 h-12 flex items-center justify-center relative bg-white rounded-2xl clay-base border-none shadow-sm overflow-hidden">
        <BurstIcon className="w-10 h-10 absolute opacity-20" />
        <span className="relative z-10 text-lg font-num font-black text-slate-800">{v}</span>
      </div>
    ))}
    <motion.div 
      animate={{ opacity: [0.4, 0.8, 0.4] }}
      transition={{ repeat: Infinity, duration: 1.5 }}
      className="absolute inset-2 border-4 border-dashed border-amber-400 rounded-2xl bg-amber-400/5"
    />
  </div>
);

const VisualApple2 = () => (
  <div className="w-40 space-y-3">
    <div className="flex justify-between text-[12px] font-black text-slate-500 tracking-wider">
      <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> PROGRESS</span>
      <span className="text-amber-500">85/120</span>
    </div>
    <div className="h-5 bg-slate-200 rounded-full overflow-hidden border-none shadow-inner p-1">
      <motion.div 
        initial={{ width: '20%' }}
        animate={{ width: '70%' }}
        transition={{ repeat: Infinity, duration: 3, repeatType: 'reverse' }}
        className="h-full bg-amber-400 rounded-full shadow-sm"
      />
    </div>
  </div>
);

const VisualGold1 = () => (
  <div className="flex gap-3 p-4">
    <div className="w-16 h-16 bg-white rounded-[24px] flex items-center justify-center text-3xl font-num clay-base text-slate-900 border-none shadow-lg relative">
      <GoldRushIcon className="w-10 h-10 absolute opacity-10" />
      <span className="relative z-10">4</span>
      <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="absolute -bottom-2 -right-2">
        <MousePointer2 className="w-8 h-8 text-slate-900 fill-white drop-shadow-lg" />
      </motion.div>
    </div>
    <div className="w-16 h-16 bg-white rounded-[24px] flex items-center justify-center text-3xl font-num clay-base text-slate-900 border-none shadow-lg">6</div>
  </div>
);

const VisualGold2 = () => (
  <div className="relative p-4">
    <div className="w-20 h-20 bg-amber-400 rounded-[32px] flex items-center justify-center text-4xl font-num text-white clay-base border-none shadow-[0_0_30px_rgba(251,191,36,0.6)]">
      5
    </div>
    <Sparkles className="absolute -top-4 -right-4 w-12 h-12 text-amber-500 animate-pulse" />
  </div>
);

const INSTRUCTIONS: Record<'duel' | 'sum10' | 'apple' | 'goldrush', Step[]> = {
  duel: [
    {
      title: "Cloud Mach",
      description: "Solve the math problem at the top as fast as possible. Compare the left and right values!",
      visual: <VisualDuel1 />
    },
    {
      title: "Tap Greater",
      description: "Tap the side with the higher value. Fast correct answers build your combo!",
      visual: <VisualDuel2 />
    },
    {
      title: "Sonic Overload",
      description: "Get 10 correct answers to trigger FEVER! Enjoy x2 points and ultra-fast question advance for 15s.",
      visual: <VisualFever mode="duel" />
    }
  ],
  sum10: [
    {
      title: "Shell Crush",
      description: "Connect adjacent bubbles to sum to a multiple of 10 (10, 20, 30...).",
      visual: <VisualSum1 />
    },
    {
      title: "The Blast (9)",
      description: "Include a '9' in your path to clear surrounding bubbles in a massive cross blast!",
      visual: <VisualSum2 />
    },
    {
      title: "Pearl Fever",
      description: "Reach 10 stacks to activate FEVER! Score x2 points and '9' blasts now clear all 8 directions!",
      visual: <VisualFever mode="sum10" />
    }
  ],
  apple: [
    {
      title: "Star Burst",
      description: "Drag a box over any group of stars. The sum of all numbers inside must be exactly 10.",
      visual: <VisualApple1 />
    },
    {
      title: "Clear Board",
      description: "Try to clear all 120 blocks before time runs out!",
      visual: <VisualApple2 />
    }
  ],
  goldrush: [
    {
      title: "Target Match",
      description: "Connect tiles to match the Target Number. The target increases with every successful match!",
      visual: <VisualGold1 />
    },
    {
      title: "Gold Nuggets",
      description: "Use Gold Tiles to reach the target faster. They never lock out and provide huge point bonuses!",
      visual: <VisualGold2 />
    },
    {
      title: "Gold Rush",
      description: "Hit 5 targets to trigger Gold Rush! Target locks to 10, and spamming Gold tiles gives up to 5x points!",
      visual: <VisualFever mode="goldrush" />
    }
  ]
};

export default function HowToPlay({ mode, onClose }: HowToPlayProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const steps = INSTRUCTIONS[mode];

  const handleFinish = () => {
    if (dontShowAgain) {
      localStorage.setItem(`hide_how_to_${mode}`, 'true');
    }
    onClose();
  };

  return (
    <div className="absolute inset-0 z-[500] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="w-full max-w-sm p-8 bg-white rounded-[48px] shadow-[0_30px_60px_rgba(0,0,0,0.3)] border-b-[12px] border-slate-200 ring-8 ring-white/20 relative overflow-hidden"
      >
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 p-2 text-slate-300 hover:text-slate-900 transition-colors z-20"
        >
          <X className="w-8 h-8" strokeWidth={3} />
        </button>

        <div className="relative h-72 mb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ x: 50, opacity: 0, scale: 0.9 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              exit={{ x: -50, opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 20 }}
              className="absolute inset-0 flex flex-col items-center justify-center text-center"
            >
              <div className="mb-8 p-8 bg-slate-50 rounded-[40px] shadow-inner border border-slate-100 relative">
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 2px, transparent 2px)', backgroundSize: '12px 12px' }} />
                {steps[currentStep].visual}
              </div>
              <h3 className="text-3xl font-num font-black mb-3 tracking-tight text-slate-800 uppercase italic">
                {steps[currentStep].title}
              </h3>
              <p className="text-slate-500 text-sm font-bold leading-relaxed px-4">
                {steps[currentStep].description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mb-10">
          {steps.map((_, i) => (
            <div 
              key={i}
              className={cn(
                "h-2.5 rounded-full transition-all duration-500",
                i === currentStep ? "w-10 bg-amber-400 shadow-lg shadow-amber-400/40" : "w-2.5 bg-slate-200"
              )}
            />
          ))}
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            {currentStep > 0 ? (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 rounded-3xl font-black py-4 flex items-center justify-center gap-2 transition-all shadow-md border-b-[8px] border-slate-200 bg-slate-100 text-slate-400 active:border-b-[0px] active:translate-y-[8px]"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={3} />
                BACK
              </button>
            ) : <div className="flex-1" />}

            {currentStep < steps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(prev => prev + 1)}
                className="flex-1 rounded-3xl font-black py-4 flex items-center justify-center gap-2 transition-all shadow-[0_15px_30px_rgba(14,165,233,0.3)] border-b-[8px] border-sky-600 bg-sky-400 text-white active:border-b-[0px] active:translate-y-[8px]"
              >
                NEXT
                <ChevronRight className="w-5 h-5" strokeWidth={3} />
              </button>
            ) : (
              <button
                onClick={handleFinish}
                className="flex-1 rounded-3xl font-black py-4 flex items-center justify-center gap-2 transition-all shadow-[0_15px_30px_rgba(16,185,129,0.3)] border-b-[8px] border-emerald-600 bg-emerald-400 text-white active:border-b-[0px] active:translate-y-[8px]"
              >
                READY!
              </button>
            )}
          </div>

          <label className="flex items-center justify-center gap-3 cursor-pointer group py-2">
            <input 
              type="checkbox" 
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="hidden"
            />
            <div className={cn(
              "w-7 h-7 rounded-xl border-[4px] flex items-center justify-center transition-all",
              dontShowAgain ? "bg-amber-400 border-slate-800" : "border-slate-200 group-hover:border-slate-300 bg-white"
            )}>
              {dontShowAgain && <CheckCircle2 className="w-5 h-5 text-slate-800" strokeWidth={3} />}
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Skip instructions next time</span>
          </label>
        </div>
      </motion.div>
    </div>
  );
}
